import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useAnalyticsStore from '../stores/useAnalyticsStore';
import LineChart from '../components/LineChart';
import BarChart from '../components/BarChart';
import DashboardLayout from '../components/DashboardLayout';
import useAuthStore from '../stores/useAuthStore';
import exportToPDF from '../utils/exportToPDF';

const InstructorDashboard = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const {
    instructorOverview,
    coursePerformance,
    dailyMetrics,
    fetchInstructorOverview,
    fetchCoursePerformance,
    fetchDailyMetrics,
    isLoading,
    error
  } = useAnalyticsStore();

  const [activeTab, setActiveTab] = useState('overview');

  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // 30 days ago
    endDate: new Date()
  });

  const exportCoursePerformance = () => {
    if (!coursePerformance || coursePerformance.length === 0) {
      alert("No course performance data available to export.");
      return;
    }

    const columns = ['Course Title', 'Category', 'Enrollments', 'Revenue'];
    const rows = coursePerformance.map(course => [
      course?.title,
      course?.category?.name,
      course?.totalEnrollments,
      `₦${course?.revenue?.toLocaleString()}`
    ]);
    exportToPDF(coursePerformance, 'Course_Performance_Report', columns, rows);
  };

  const exportDailyMetrics = (metricType, data) => {
    let columns, rows, title;

    if (metricType === 'revenue') {
      columns = ['Date', 'Revenue'];
      rows = data.map(item => [item.date, `${item?.amount?.toLocaleString()}`]);
      title = 'Daily_Revenue_Report';
    } else if (metricType === 'enrollments') {
      columns = ['Date', 'Enrollments'];
      rows = data.map(item => [item.date, item.count]);
      title = 'Daily_Enrollments_Report';
    } else if (metricType === 'completions') {
      columns = ['Date', 'Completions'];
      rows = data.map(item => [item.date, item.count]);
      title = 'Daily_Completions_Report';
    } else if (metricType === 'users') {
      columns = ['Date', 'New Users'];
      rows = data.map(item => [item.date, item.count]);
      title = 'Daily_Users_Report';
    }

    exportToPDF(data, title, columns, rows);
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    fetchDailyMetrics(range.startDate, range.endDate);
  };

  useEffect(() => {
    fetchInstructorOverview();
    fetchCoursePerformance();
    fetchDailyMetrics(dateRange.startDate, dateRange.endDate);
  }, [dateRange, fetchCoursePerformance, fetchDailyMetrics, fetchInstructorOverview]);

  if (isLoading) {
    return (
      <DashboardLayout userRole={user.role}>
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout userRole={user.role}>
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">Error loading dashboard</h2>
            <p className="text-neutral-600 dark:text-neutral-400 mb-4">{error}</p>
            <button
              onClick={() => {
                fetchInstructorOverview();
                fetchCoursePerformance();
                fetchDailyMetrics();
              }}
              className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              Retry
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userRole={user.role}>
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="mb-8 flex flex-wrap justify-between">
            <div>
              <h1 className="text-3xl font-bold text-neutral-900 dark:text-white mb-2">Instructor Dashboard</h1>
              <p className="text-neutral-600 dark:text-neutral-400">
                Track your course performance, revenue, and student engagement
              </p>
            </div>

            <div className="flex gap-3 items-center justify-end">
              <h1 className="text-md font-bold text-neutral-900 dark:text-white">Partner Program</h1>
              <Link to={'/instructor/partnerprogram'} className="text-success-500 hover:text-success-600 text-sm font-medium underline">
                Aply Now
              </Link>
            </div>
          </div>


          {/* KPI Stats */}
          {instructorOverview && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mr-4">
                    <span className="text-2xl">🎓</span>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Total Courses</p>
                    <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                      {instructorOverview.totalCourses}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-success-100 dark:bg-success-900/30 rounded-full flex items-center justify-center mr-4">
                    <span className="text-2xl">👥</span>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Total Enrolled</p>
                    <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                      {instructorOverview.totalStudents}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-warning-100 dark:bg-warning-900/30 rounded-full flex items-center justify-center mr-4">
                    <span className="text-2xl">📈</span>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Total Revenue</p>
                    <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                      ₦{instructorOverview?.totalRevenue?.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-warning-100 dark:bg-warning-900/30 rounded-full flex items-center justify-center mr-4">
                    <span className="text-2xl">💰</span>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Wallet Balance</p>
                    <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                      ₦{instructorOverview?.walletBalance?.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-warning-100 dark:bg-warning-900/30 rounded-full flex items-center justify-center mr-4">
                    <span className="text-2xl">💵</span>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Total Earnings</p>
                    <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                      ₦{instructorOverview?.totalEarnings?.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-warning-100 dark:bg-warning-900/30 rounded-full flex items-center justify-center mr-4">
                    <span className="text-2xl">💸</span>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Total Payout</p>
                    <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                      ₦{instructorOverview?.totalWithdrawn?.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-info-100 dark:bg-info-900/30 rounded-full flex items-center justify-center mr-4">
                    <span className="text-2xl">⭐</span>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Course Ratings</p>
                    <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                      {instructorOverview.avgCourseRating}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-info-100 dark:bg-info-900/30 rounded-full flex items-center justify-center mr-4">
                    <span className="text-2xl">✅</span>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Approved Courses</p>
                    <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                      {instructorOverview.totalApprovedCourses}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mb-8 bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Select Date Range</h3>
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-4 sm:space-y-0">
              <div className="flex-1">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Start Date</label>
                <input
                  type="date"
                  value={dateRange.startDate.toISOString().split('T')[0]}
                  onChange={(e) => handleDateRangeChange({
                    ...dateRange,
                    startDate: new Date(e.target.value)
                  })}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">End Date</label>
                <input
                  type="date"
                  value={dateRange.endDate.toISOString().split('T')[0]}
                  onChange={(e) => handleDateRangeChange({
                    ...dateRange,
                    endDate: new Date(e.target.value)
                  })}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => {
                    // Reset to last 30 days
                    const now = new Date();
                    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                    setDateRange({
                      startDate: thirtyDaysAgo,
                      endDate: now
                    });
                    fetchDailyMetrics(thirtyDaysAgo, now);
                  }}
                  className="px-4 mt-6 py-2 bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 text-neutral-700 dark:text-neutral-300 text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-neutral-500"
                >
                  Reset to 30 Days
                </button>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mb-8 border-b border-neutral-200 dark:border-neutral-700">
            <nav className="-mb-px flex space-x-8">
              {['overview', 'courses', 'daily-metrics'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${activeTab === tab
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
                    }`}
                >
                  {tab.replace('-', ' ')}
                </button>
              ))}
            </nav>
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Revenue Over Time */}
              {dailyMetrics?.revenue && (
                <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                  <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">Revenue Over Time</h2>
                  <LineChart
                    data={{
                      labels: dailyMetrics?.revenue?.map(item => item._id),
                      datasets: [
                        {
                          label: 'Daily Revenue',
                          data: dailyMetrics?.revenue?.map(item => item.amount),
                          borderColor: '#10b981',
                          backgroundColor: 'rgba(16, 185, 129, 0.1)',
                          fill: true
                        }
                      ]
                    }}
                    options={{
                      scales: {
                        y: {
                          ticks: {
                            callback: function (value) {
                              return `₦${value?.toLocaleString()}`;
                            }
                          }
                        }
                      }
                    }}
                    height={300}
                  />
                </div>
              )}

              {/* Enrollments Over Time */}
              {dailyMetrics?.enrollments && (
                <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                  <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">Enrollments Over Time</h2>
                  <LineChart
                    data={{
                      labels: dailyMetrics?.enrollments?.map(item => item._id),
                      datasets: [
                        {
                          label: 'Daily Enrollments',
                          data: dailyMetrics?.enrollments?.map(item => item.count),
                          borderColor: '#3b82f6',
                          backgroundColor: 'rgba(59, 130, 246, 0.1)',
                          fill: true
                        }
                      ]
                    }}
                    height={300}
                  />
                </div>
              )}

              {/* Completed Courses Over Time */}
              {dailyMetrics?.completions && (
                <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                  <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">Completed Courses Over Time</h2>
                  <LineChart
                    data={{
                      labels: dailyMetrics?.completions?.map(item => item._id),
                      datasets: [
                        {
                          label: 'Daily Completions',
                          data: dailyMetrics?.completions?.map(item => item.count),
                          borderColor: '#f59e0b',
                          backgroundColor: 'rgba(245, 158, 11, 0.1)',
                          fill: true
                        }
                      ]
                    }}
                    height={300}
                  />
                </div>
              )}
            </div>
          )}

          {/* Courses Tab */}
          {activeTab === 'courses' && (
            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">Course Performance</h2>
                <button
                  onClick={exportCoursePerformance}
                  className="px-4 py-2 bg-success-500 hover:bg-success-600 text-white text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-success-500"
                >
                  Export as PDF
                </button>
              </div>
              {coursePerformance && coursePerformance.length > 0 ? (
                <div className="space-y-6">
                  {/* Bar Chart: Courses by Enrollments */}
                  <div className="mb-8">
                    <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">Courses by Enrollments</h3>
                    <BarChart
                      data={{
                        labels: coursePerformance.map(course => course.title),
                        datasets: [
                          {
                            label: 'Enrollments',
                            data: coursePerformance.map(course => course.totalEnrollments),
                            backgroundColor: '#3b82f6'
                          }
                        ]
                      }}
                      options={{
                        plugins: {
                          title: {
                            display: false
                          }
                        }
                      }}
                      height={400}
                    />
                  </div>

                  {/* Table: Detailed Course Metrics */}
                  <div className="overflow-x-auto lg:overflow-visible">
                    <table className="hidden min-w-full divide-y divide-neutral-200 dark:divide-neutral-700 lg:table">
                      <thead className="bg-neutral-50 dark:bg-neutral-700/50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Course</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Enrollments</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Revenue</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-neutral-800 divide-y divide-neutral-200 dark:divide-neutral-700">
                        {coursePerformance?.map((course) => (
                          <tr key={course?._id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-neutral-900 dark:text-white">{course?.title}</div>
                              <div className="text-sm text-neutral-500 dark:text-neutral-400">{course?.category?.name}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-neutral-900 dark:text-white">{course?.totalEnrollments}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-neutral-900 dark:text-white">₦{course?.revenue?.toLocaleString()}</div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Mobile: Stacked Cards */}
                    <div className="lg:hidden space-y-4">
                      {coursePerformance?.map((course) => (
                        <div key={course?._id} className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="font-medium text-neutral-900 dark:text-white text-sm">{course?.title}</h3>
                              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">{course?.category?.name}</p>
                            </div>
                          </div>
                          <div className="mt-3 grid grid-cols-2 gap-3">
                            <div>
                              <p className="text-xs text-neutral-500 dark:text-neutral-400">Enrollments</p>
                              <p className="text-sm font-medium text-neutral-900 dark:text-white">{course?.totalEnrollments}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-neutral-500 dark:text-neutral-400">Revenue</p>
                              <p className="text-sm font-medium text-neutral-900 dark:text-white">₦{course?.revenue?.toLocaleString()}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>


                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">📊</div>
                  <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">No courses created yet</h3>
                  <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                    Create your first course to start tracking performance
                  </p>
                  <button
                    onClick={() => navigate('/instructor/course/builder')}
                    className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    Create Course
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Daily Metrics Tab */}
          {activeTab === 'daily-metrics' && (
            <div className="space-y-8">
              {dailyMetrics && (
                <>
                  {/* Users Per Day */}
                  <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">Users Per Day</h2>
                      <button
                        onClick={() => exportDailyMetrics('users', dailyMetrics?.users)}
                        className="px-4 py-2 bg-success-500 hover:bg-success-600 text-white text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-success-500"
                      >
                        Export as PDF
                      </button>
                    </div>
                    <LineChart
                      data={{
                        labels: dailyMetrics?.users?.map(item => item.date),
                        datasets: [
                          {
                            label: 'New Users',
                            data: dailyMetrics?.users?.map(item => item.count),
                            borderColor: '#8b5cf6',
                            backgroundColor: 'rgba(139, 92, 246, 0.1)',
                            fill: true
                          }
                        ]
                      }}
                      height={300}
                    />
                  </div>

                  {/* Completed Courses Per Day */}
                  <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">Completed Courses Per Day</h2>
                      <button
                        onClick={() => exportDailyMetrics('completions', dailyMetrics?.completions)}
                        className="px-4 py-2 bg-success-500 hover:bg-success-600 text-white text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-success-500"
                      >
                        Export as PDF
                      </button>
                    </div>
                    <LineChart
                      data={{
                        labels: dailyMetrics?.completions?.map(item => item.date),
                        datasets: [
                          {
                            label: 'Completed Courses',
                            data: dailyMetrics?.completions?.map(item => item.count),
                            borderColor: '#f59e0b',
                            backgroundColor: 'rgba(245, 158, 11, 0.1)',
                            fill: true
                          }
                        ]
                      }}
                      height={300}
                    />
                  </div>

                  {/* Revenue Per Day */}
                  <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">Revenue Per Day</h2>
                      <button
                        onClick={() => exportDailyMetrics('revenue', dailyMetrics?.revenue)}
                        className="px-4 py-2 bg-success-500 hover:bg-success-600 text-white text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-success-500"
                      >
                        Export as PDF
                      </button>
                    </div>
                    <LineChart
                      data={{
                        labels: dailyMetrics?.revenue?.map(item => item.date),
                        datasets: [
                          {
                            label: 'Daily Revenue',
                            data: dailyMetrics?.revenue?.map(item => item.amount),
                            borderColor: '#10b981',
                            backgroundColor: 'rgba(16, 185, 129, 0.1)',
                            fill: true
                          }
                        ]
                      }}
                      options={{
                        scales: {
                          y: {
                            ticks: {
                              callback: function (value) {
                                return `₦${value.toLocaleString()}`;
                              }
                            }
                          }
                        }
                      }}
                      height={300}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default InstructorDashboard;